// The blueprint Step 1 draws: the page's own steps with every shared edit applied, plus
// the steps someone added on the page (useAmazonBlueprint).
import type {
  BlueprintEdit,
  BlueprintSide,
} from "../../api/hooks/useAmazonBlueprint";
import { CHAPTERS, STORY_STEPS } from "./chapters";
import { LANES, type LaneId, type Owner, type PathStep, STEPS } from "./pathSteps";

export interface StoryStep extends PathStep {
  /** Suggested, and not yet agreed by both sides. */
  proposed: boolean;
  /** Added on the page rather than one of the page's own steps. */
  custom: boolean;
  /** One of the page's own steps that started as a suggestion. */
  suggested: boolean;
  hidden: boolean;
  /** Which side proposed it, where the page records one. */
  by: BlueprintSide | null;
  /** The version of its shared edit; 0 where nobody has changed it. */
  version: number;
  /** Who suggested it and who agreed it, by email, with the day; null where unknown. */
  suggestedBy: string | null;
  suggestedAt: string | null;
  agreedBy: string | null;
  agreedAt: string | null;
}

const OWNERS: Owner[] = ["inrange", "amazon", "third", "both"];
const laneOf = (lane: string | null): LaneId | null =>
  LANES.find((l) => l.id === lane)?.id ?? null;
const chapterOf = (chapter: number | null): number | null =>
  chapter != null && chapter >= 0 && chapter < CHAPTERS.length ? chapter : null;

const base = (step: PathStep): StoryStep => ({
  ...step,
  proposed: false,
  suggested: false,
  custom: false,
  hidden: false,
  by: null,
  version: 0,
  suggestedBy: null,
  suggestedAt: null,
  agreedBy: null,
  agreedAt: null,
});

const attribution = (edit: BlueprintEdit) => ({
  suggestedBy: edit.suggested_by ?? null,
  suggestedAt: edit.suggested_at ?? null,
  agreedBy: edit.agreed_by ?? null,
  agreedAt: edit.agreed_at ?? null,
});

const applied = (step: StoryStep, edit: BlueprintEdit): StoryStep => ({
  ...step,
  title: edit.title ?? step.title,
  desc: edit.detail ?? step.desc,
  owner: edit.owner && OWNERS.includes(edit.owner) ? edit.owner : step.owner,
  // A changed owner drops the page's own label for the old one ("DNO", "Landlord").
  ownerLabel: edit.owner && edit.owner !== step.owner ? undefined : step.ownerLabel,
  lane: laneOf(edit.lane) ?? step.lane,
  chapter: chapterOf(edit.chapter) ?? step.chapter,
  // A saved list is the whole of what it waits on, so the page's end-dependencies go too.
  ...(edit.deps ? { deps: edit.deps, endDeps: [] } : {}),
  needs: edit.needs ?? step.needs,
  subs: edit.subs ?? step.subs,
  proposed: edit.status ? edit.status === "proposed" : step.proposed,
  by: edit.proposed_by ?? step.by,
  hidden: edit.hidden,
  version: edit.version,
  ...attribution(edit),
});

const added = (edit: BlueprintEdit): StoryStep | null => {
  const lane = laneOf(edit.lane);
  const chapter = chapterOf(edit.chapter);
  if (!lane || chapter == null) return null;
  return {
    id: edit.step_id,
    lane,
    chapter,
    owner: edit.owner ?? "inrange",
    title: edit.title ?? "Untitled step",
    desc: edit.detail ?? "",
    deps: edit.deps ?? [],
    days: null,
    subs: edit.subs ?? [],
    needs: edit.needs ?? [],
    source: "",
    proposed: edit.status !== "agreed",
    suggested: true,
    custom: true,
    hidden: edit.hidden,
    by: edit.proposed_by ?? "inrange",
    version: edit.version,
    ...attribution(edit),
  };
};

/** Every step Step 1 can show, our internal ones left out, with ``edits`` applied. */
export const blueprintSteps = (edits: BlueprintEdit[]): StoryStep[] => {
  const byId = new Map(edits.map((e) => [e.step_id, e]));
  const own = [...STEPS.filter((s) => !s.internal), ...STORY_STEPS].map((s) => {
    const edit = byId.get(s.id);
    return edit ? applied(base(s), edit) : base(s);
  });
  const ownIds = new Set(own.map((s) => s.id));
  const extra = edits
    .filter((e) => e.kind === "custom" && !ownIds.has(e.step_id))
    .map(added)
    .filter((s): s is StoryStep => s !== null);
  return [...own, ...extra];
};
